/**
 * Decides, from a stream of microphone loudness samples, when the user starts
 * and finishes a phrase — the "hands-free" part of a voice conversation.
 *
 * Pure and clock-injected so it can be tested without a microphone.
 */

export type VadEvent = 'speech-started' | 'speech-ended' | 'discard' | 'rotate';

export interface VadConfig {
  /** Quietest level ever treated as speech (RMS 0..1). */
  minSpeechRms: number;
  /** Speech must exceed the measured room noise by this factor. */
  noiseFactor: number;
  /** Loud for this long before a phrase counts as started (ignores clicks). */
  startMs: number;
  /** Quiet for this long after speech → the phrase is over. */
  silenceMs: number;
  /** Shorter phrases are treated as noise and dropped. */
  minSpeechMs: number;
  /** Hard cap for one phrase; the recording is cut here. */
  maxUtteranceMs: number;
  /** While nobody speaks, ask for a fresh recording this often (keeps silence out of the bill). */
  rotateIdleMs: number;
}

export const DEFAULT_VAD_CONFIG: VadConfig = {
  minSpeechRms: 0.02,
  noiseFactor: 3,
  startMs: 120,
  silenceMs: 1100,
  minSpeechMs: 300,
  maxUtteranceMs: 55_000,
  rotateIdleMs: 4_000,
};

export class VoiceActivityDetector {
  private noiseFloor = 0.005;
  private speaking = false;
  private loudSince: number | null = null;
  private speechStartedAt = 0;
  private lastLoudAt = 0;
  private idleSince: number;

  constructor(
    nowMs: number,
    private readonly config: VadConfig = DEFAULT_VAD_CONFIG,
  ) {
    this.idleSince = nowMs;
  }

  get inSpeech(): boolean {
    return this.speaking;
  }

  /**
   * Treat the user as already mid-phrase (they spoke over the assistant): the
   * phrase then ends on the usual pause instead of waiting for a fresh start.
   */
  forceSpeech(nowMs: number, startedAtMs: number = nowMs): void {
    this.speaking = true;
    this.loudSince = null;
    this.speechStartedAt = startedAtMs;
    this.lastLoudAt = nowMs;
  }

  /** Start over for the next phrase (keeps what was learned about the room noise). */
  reset(nowMs: number): void {
    this.speaking = false;
    this.loudSince = null;
    this.idleSince = nowMs;
  }

  push(rms: number, nowMs: number): VadEvent | null {
    const threshold = Math.max(this.config.minSpeechRms, this.noiseFloor * this.config.noiseFactor);
    const loud = rms > threshold;

    if (!this.speaking) {
      // Learn the room: only quiet samples, capped so a loud room is not "normal" forever.
      if (!loud) this.noiseFloor = Math.min(0.05, this.noiseFloor * 0.95 + rms * 0.05);

      if (loud) {
        this.loudSince ??= nowMs;
        if (nowMs - this.loudSince >= this.config.startMs) {
          this.speaking = true;
          this.speechStartedAt = this.loudSince;
          this.lastLoudAt = nowMs;
          return 'speech-started';
        }
      } else {
        this.loudSince = null;
      }
      if (nowMs - this.idleSince >= this.config.rotateIdleMs) {
        this.idleSince = nowMs;
        return 'rotate';
      }
      return null;
    }

    if (loud) this.lastLoudAt = nowMs;
    if (nowMs - this.speechStartedAt >= this.config.maxUtteranceMs) {
      this.finish(nowMs);
      return 'speech-ended';
    }
    if (nowMs - this.lastLoudAt >= this.config.silenceMs) {
      const spoke = this.lastLoudAt - this.speechStartedAt;
      this.finish(nowMs);
      return spoke >= this.config.minSpeechMs ? 'speech-ended' : 'discard';
    }
    return null;
  }

  private finish(nowMs: number): void {
    this.speaking = false;
    this.loudSince = null;
    this.idleSince = nowMs;
  }
}

/** Loudness of one analyser frame (time-domain bytes centred on 128). */
export function rmsOfByteFrame(frame: ArrayLike<number>): number {
  if (frame.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i++) {
    const v = (frame[i]! - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / frame.length);
}
